import { Link, useLocation } from 'react-router-dom';
import { useState, useEffect, useCallback, useRef } from 'react';
import { useSettings } from '@/lib/useSettings';

/**
 * Site header — shared by Home, /studio, /eternity.
 * Wordmark left · nav + social icons right. Transparent over hero, solid after
 * scroll, and luminance-adaptive: text flips to dark when the band beneath the
 * header is light (so it stays legible over paper sections).
 */

const NAV = [
    { to: '/', label: 'HOME' },
    { to: '/eternity', label: 'ETERNITY' },
    { to: '/studio', label: 'STUDIO' },
];

const YT = (
    <svg viewBox="0 3.6 24 16.8" fill="currentColor" aria-hidden="true">
        <path d="M23.5 6.2a3 3 0 0 0-2.1-2.1C19.5 3.6 12 3.6 12 3.6s-7.5 0-9.4.5A3 3 0 0 0 .5 6.2 31.3 31.3 0 0 0 0 12a31.3 31.3 0 0 0 .5 5.8 3 3 0 0 0 2.1 2.1c1.9.5 9.4.5 9.4.5s7.5 0 9.4-.5a3 3 0 0 0 2.1-2.1A31.3 31.3 0 0 0 24 12a31.3 31.3 0 0 0-.5-5.8zM9.6 15.6V8.4l6.2 3.6z" />
    </svg>
);
const IG = (
    <svg viewBox="1 1 22 22" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
        <rect x="2" y="2" width="20" height="20" rx="5" />
        <circle cx="12" cy="12" r="4.2" />
        <circle cx="17.4" cy="6.6" r="1.2" fill="currentColor" stroke="none" />
    </svg>
);
const TT = (
    <svg viewBox="0.6 1.5 16 19.13" fill="currentColor" aria-hidden="true">
        <path d="M16.6 5.8a4.8 4.8 0 0 1-1-.1 4.9 4.9 0 0 1-2-1.3 4.7 4.7 0 0 1-1.2-2.6V1.5h-3v13.2a2.8 2.8 0 1 1-2-2.7V8.9a5.9 5.9 0 1 0 5 5.8V8.4a7.8 7.8 0 0 0 4.2 1.2V6.6a4.8 4.8 0 0 1-1-.8z" />
    </svg>
);

const X = (
    <svg viewBox="1.25 2.25 21.57 19.5" fill="currentColor" aria-hidden="true">
        <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24h-6.66l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231 5.45-6.231zm-1.161 17.52h1.833L7.084 4.126H5.117l11.966 15.644z" />
    </svg>
);

const SOCIALS: Array<{ key: 'socialYoutube' | 'socialInstagram' | 'socialTiktok' | 'socialTwitter'; label: string; icon: JSX.Element }> = [
    { key: 'socialYoutube', label: 'YouTube', icon: YT },
    { key: 'socialInstagram', label: 'Instagram', icon: IG },
    { key: 'socialTiktok', label: 'TikTok', icon: TT },
    { key: 'socialTwitter', label: 'X', icon: X },
];

/** Relative luminance of an "r, g, b" computed-color string (0 dark – 1 light). */
function luminance(rgb: string): number {
    const m = rgb.match(/\d+(\.\d+)?/g);
    if (!m || m.length < 3) return 0;
    const [r, g, b] = m.map(Number);
    return (0.299 * r + 0.587 * g + 0.114 * b) / 255;
}

const Header = () => {
    const [scrolled, setScrolled] = useState(false);
    const [light, setLight] = useState(false);
    const [open, setOpen] = useState(false);
    const location = useLocation();
    const settings = useSettings();

    // Mirror `open` into a ref so `sample` can read it without depending on it —
    // otherwise toggling the menu re-creates `sample`, which re-fires the
    // route-change effect below and immediately closes the menu again.
    const openRef = useRef(open);
    openRef.current = open;

    // Sample the band directly beneath the header and adapt text colour to it.
    const sample = useCallback(() => {
        if (openRef.current) return;
        const x = Math.floor(window.innerWidth / 2);
        let el = document.elementFromPoint(x, 72) as HTMLElement | null;
        while (el) {
            const bg = getComputedStyle(el).backgroundColor;
            const m = bg.match(/[\d.]+/g);
            if (m && (m.length < 4 || Number(m[3]) > 0.2) && bg !== 'rgba(0, 0, 0, 0)') {
                setLight(luminance(bg) > 0.6);
                return;
            }
            el = el.parentElement;
        }
        setLight(false);
    }, []);

    useEffect(() => {
        let raf = 0;
        const onScroll = () => {
            setScrolled(window.scrollY > 20);
            cancelAnimationFrame(raf);
            raf = requestAnimationFrame(sample);
        };
        window.addEventListener('scroll', onScroll, { passive: true });
        window.addEventListener('resize', sample);
        const t = setTimeout(sample, 80);
        return () => {
            window.removeEventListener('scroll', onScroll);
            window.removeEventListener('resize', sample);
            cancelAnimationFrame(raf);
            clearTimeout(t);
        };
    }, [sample]);

    // Re-sample after route changes (new page, new band under the header).
    useEffect(() => {
        setOpen(false);
        const t = setTimeout(sample, 120);
        return () => clearTimeout(t);
    }, [location.pathname, sample]);

    return (
        <header className={`mx-header ${scrolled ? 'scrolled' : ''} ${light && !scrolled ? 'light' : ''}`}>
            <Link to="/" className="mx-header-mark">
                MAXIMUM
            </Link>

            <button
                className={`mx-burger ${open ? 'open' : ''}`}
                onClick={() => setOpen(!open)}
                aria-label="Toggle menu"
                aria-expanded={open}
            >
                <span />
                <span />
                <span />
            </button>

            <nav className={`mx-nav ${open ? 'open' : ''}`}>
                {NAV.map((item) => (
                    <Link
                        key={item.to}
                        to={item.to}
                        className={`mx-nav-link ${location.pathname === item.to ? 'active' : ''}`}
                    >
                        {item.label}
                    </Link>
                ))}
                <span className="mx-nav-divider" aria-hidden="true" />
                <div className="mx-nav-socials">
                    {SOCIALS.filter((s) => settings[s.key]).map((s) => (
                        <a
                            key={s.key}
                            href={settings[s.key]}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="mx-nav-social"
                            aria-label={s.label}
                        >
                            {s.icon}
                        </a>
                    ))}
                </div>
            </nav>

            <style>{`
                .mx-header {
                    position: fixed;
                    top: 0;
                    left: 0;
                    right: 0;
                    z-index: var(--z-sticky);
                    display: flex;
                    align-items: center;
                    justify-content: space-between;
                    height: 64px;
                    padding: 0 clamp(1rem, 4vw, 3rem);
                    background: transparent;
                    transition: background var(--transition-base), color var(--transition-base);
                    --ink: #fff;
                    --ink-dim: rgba(255, 255, 255, 0.72);
                    --line: rgba(255, 255, 255, 0.25);
                    color: var(--ink);
                }
                .mx-header.scrolled {
                    background: rgba(0, 0, 0, 0.92);
                    backdrop-filter: blur(10px);
                    -webkit-backdrop-filter: blur(10px);
                    border-bottom: 1px solid rgba(255, 255, 255, 0.08);
                }
                /* Over a light band (not scrolled): flip ink to dark */
                .mx-header.light {
                    --ink: #0a0a0a;
                    --ink-dim: rgba(10, 10, 10, 0.66);
                    --line: rgba(10, 10, 10, 0.22);
                }

                .mx-header-mark {
                    font-family: var(--font-family);
                    font-weight: 900;
                    font-size: 1rem;
                    letter-spacing: 0.14em;
                    color: var(--ink);
                    /* keep the wordmark above the sliding mobile menu */
                    position: relative;
                    z-index: 2;
                }
                .mx-header-mark:hover { color: var(--color-accent-primary); }

                .mx-nav {
                    display: flex;
                    align-items: center;
                    gap: clamp(1rem, 2.4vw, 2.2rem);
                }
                .mx-nav-link {
                    font-family: var(--font-family);
                    font-weight: 600;
                    font-size: 0.78rem;
                    letter-spacing: 0.14em;
                    color: var(--ink-dim);
                    transition: color var(--transition-fast);
                }
                .mx-nav-link:hover { color: var(--ink); }
                .mx-nav-link.active { color: var(--color-accent-secondary); }

                .mx-nav-divider {
                    width: 1px;
                    height: 16px;
                    background: var(--line);
                }
                .mx-nav-socials {
                    display: flex;
                    align-items: center;
                    gap: 0.9rem;
                }
                .mx-nav-social {
                    color: var(--ink-dim);
                    display: inline-flex;
                    align-items: center;
                    transition: color var(--transition-fast), transform var(--transition-fast);
                }
                /* size by a common glyph height (viewBoxes are cropped tight), so
                   each icon's own whitespace doesn't skew the spacing */
                .mx-nav-social svg { height: 15px; width: auto; display: block; }
                .mx-nav-social:hover {
                    color: var(--color-accent-secondary);
                    transform: translateY(-1px);
                }

                .mx-burger {
                    display: none;
                    flex-direction: column;
                    gap: 5px;
                    background: none;
                    border: none;
                    cursor: pointer;
                    padding: 8px;
                    z-index: var(--z-modal);
                }
                .mx-burger span {
                    display: block;
                    width: 22px;
                    height: 2px;
                    background: var(--ink);
                    transition: transform var(--transition-base), opacity var(--transition-base);
                }
                .mx-burger.open span { background: #fff; }
                .mx-burger.open span:nth-child(1) { transform: translateY(7px) rotate(45deg); }
                .mx-burger.open span:nth-child(2) { opacity: 0; }
                .mx-burger.open span:nth-child(3) { transform: translateY(-7px) rotate(-45deg); }

                @media (max-width: 768px) {
                    .mx-burger { display: flex; }
                    .mx-nav {
                        position: fixed;
                        top: 64px;
                        left: 0;
                        right: 0;
                        z-index: 1; /* below the wordmark/burger (z-index 2+) */
                        flex-direction: column;
                        align-items: flex-start;
                        gap: 1.1rem;
                        padding: 1.4rem clamp(1rem, 4vw, 3rem) 1.8rem;
                        background: rgba(0, 0, 0, 0.97);
                        border-bottom: 1px solid rgba(255, 255, 255, 0.1);
                        /* fade the panel's top edge so it dissolves toward the
                           header instead of hard-butting under the wordmark */
                        -webkit-mask-image: linear-gradient(to bottom, transparent 0, #000 2.6rem);
                        mask-image: linear-gradient(to bottom, transparent 0, #000 2.6rem);
                        transform: translateY(-110%);
                        opacity: 0;
                        pointer-events: none;
                        transition: transform var(--transition-base), opacity var(--transition-fast);
                    }
                    .mx-nav.open {
                        transform: translateY(0);
                        opacity: 1;
                        pointer-events: auto;
                        /* solid while open; the top fade only applies in the
                           closed/closing state (base rule) so it reads as the
                           menu dissolving away on close */
                        -webkit-mask-image: none;
                        mask-image: none;
                    }
                    /* Mobile menu always dark panel → light ink regardless of band */
                    .mx-nav.open .mx-nav-link { color: rgba(255, 255, 255, 0.8); font-size: 0.95rem; }
                    .mx-nav.open .mx-nav-link.active { color: var(--color-accent-secondary); }
                    .mx-nav.open .mx-nav-social { color: rgba(255, 255, 255, 0.7); }
                    .mx-nav-divider { display: none; }
                    .mx-nav-socials { gap: 1.3rem; }
                }
            `}</style>
        </header>
    );
};

export default Header;
